import { useCallback,useEffect,useState } from "react";
import { Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Slider,Typography } from "@mui/material";
import Cropper from "react-easy-crop";
import { useAppTheme } from "../../../context/ThemeContext";

const createImage=src=>new Promise((resolve,reject)=>{
  const image=new Image();
  image.onload=()=>resolve(image);
  image.onerror=reject;
  image.src=src;
});

const getCroppedFile=async(src,pixelCrop,originalName="imagen.png")=>{
  const image=await createImage(src);
  const canvas=document.createElement("canvas");
  const context=canvas.getContext("2d");

  if(!context){
    throw new Error("No se pudo procesar la imagen.");
  }

  canvas.width=Math.round(pixelCrop.width);
  canvas.height=Math.round(pixelCrop.height);

  context.clearRect(0,0,canvas.width,canvas.height);

  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const blob=await new Promise((resolve,reject)=>{
    canvas.toBlob(
      value=>value?resolve(value):reject(new Error("No se pudo recortar la imagen.")),
      "image/webp",
      0.94
    );
  });

  const baseName=originalName.replace(/\.[^/.]+$/,"")||"imagen";

  return new File(
    [blob],
    `${baseName}-recortada.webp`,
    {type:"image/webp"}
  );
};

export default function PhotoCropper({
  open,
  file,
  onClose,
  onConfirm,
  aspect=3/4,
  title="Ajustar fotografía",
  description="Mueve la imagen y ajusta el zoom para seleccionar la parte que se guardará.",
  confirmText="Usar fotografía"
}){
  const {colors,typography,shape}=useAppTheme();

  const [imageSrc,setImageSrc]=useState("");
  const [crop,setCrop]=useState({x:0,y:0});
  const [zoom,setZoom]=useState(1);
  const [croppedPixels,setCroppedPixels]=useState(null);
  const [processing,setProcessing]=useState(false);

  useEffect(()=>{
    if(!file){
      setImageSrc("");
      setCroppedPixels(null);
      return;
    }

    const url=URL.createObjectURL(file);

    setImageSrc(url);
    setCrop({x:0,y:0});
    setZoom(1);
    setCroppedPixels(null);

    return ()=>URL.revokeObjectURL(url);
  },[file]);

  const handleCropComplete=useCallback((_,pixels)=>{
    setCroppedPixels(pixels);
  },[]);

  const handleConfirm=async()=>{
    if(!imageSrc||!croppedPixels||!file||processing) return;

    try{
      setProcessing(true);

      const croppedFile=await getCroppedFile(
        imageSrc,
        croppedPixels,
        file.name
      );

      await onConfirm(croppedFile);
    }catch(error){
      console.error("Error al recortar la imagen:",error);
    }finally{
      setProcessing(false);
    }
  };

  const isDocument=aspect>1;

  const cropSize=isDocument
    ?{width:500,height:312}
    :{width:300,height:400};

  return (
    <Dialog
      open={open}
      onClose={processing?undefined:onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface
        }
      }}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:600,
            color:colors.text
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt:0.3,
            fontSize:typography.sizes.sm,
            fontWeight:400,
            color:colors.textSecondary
          }}
        >
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            position:"relative",
            width:"100%",
            height:440,
            backgroundColor:colors.backgroundSoft,
            backgroundImage:`
              linear-gradient(45deg,rgba(128,128,128,0.12) 25%,transparent 25%),
              linear-gradient(-45deg,rgba(128,128,128,0.12) 25%,transparent 25%),
              linear-gradient(45deg,transparent 75%,rgba(128,128,128,0.12) 75%),
              linear-gradient(-45deg,transparent 75%,rgba(128,128,128,0.12) 75%)
            `,
            backgroundSize:"20px 20px",
            backgroundPosition:"0 0,0 10px,10px -10px,-10px 0",
            borderRadius:shape.radius,
            overflow:"hidden",
            "& .reactEasyCrop_Container":{
              backgroundColor:"transparent"
            },
            "& .reactEasyCrop_Image":{
              backgroundColor:"transparent"
            }
          }}
        >
          {imageSrc&&(
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropSize={cropSize}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
              objectFit="contain"
              style={{
                containerStyle:{
                  backgroundColor:"transparent"
                },
                mediaStyle:{
                  backgroundColor:"transparent"
                },
                cropAreaStyle:{
                  borderColor:colors.secondary
                }
              }}
            />
          )}
        </Box>

        <Box sx={{mt:2}}>
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.sm,
              fontWeight:500,
              color:colors.text
            }}
          >
            Zoom
          </Typography>

          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_,value)=>setZoom(value)}
            sx={{
              color:colors.controlActive
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px:3,
          py:2,
          borderTop:`1px solid ${colors.border}`,
          gap:1
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          disabled={processing}
          sx={{
            color:colors.buttonText,
            fontWeight:500,
            textTransform:"none"
          }}
        >
          Cancelar
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleConfirm}
          disabled={processing||!croppedPixels}
          sx={{
            backgroundColor:colors.secondary,
            color:colors.buttonPrimaryText,
            fontWeight:500,
            boxShadow:"none",
            textTransform:"none",
            "&:hover":{
              backgroundColor:colors.secondaryHover,
              boxShadow:"none"
            }
          }}
        >
          {processing?"Procesando...":confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}